"use client";
import { useEffect, type ReactNode } from "react";
import dynamic from "next/dynamic";
import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useAuth } from "@/components/providers/auth-provider";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { identityGatedRoute, shellHoldsIdentity, tempContestSlugs, tempPageAllowed } from "@/lib/contest-temp-routing";

// `SITE-SPD04`: only a contest's temporary account on its profile page shows
// these, so their code (and the profile API's) loads then, not on every page.
const ContestTempNav = dynamic(() => import("./ContestTempNav").then((m) => m.ContestTempNav), { ssr: false });
const ContestTempProfile = dynamic(() => import("./ContestTempProfile").then((m) => m.ContestTempProfile), { ssr: false });

/**
 * What a gated page shows while the first session check keeps failing for a
 * reason that proves nothing about the session: a sentence and a way to ask
 * again, instead of a skeleton with no end (a 502 during a deploy, 2026-09-24).
 */
function SessionReconnecting({ onRetry }: { onRetry: () => void }) {
  const t = useTranslations("common");
  return (
    <div aria-busy="true" className="space-y-4 p-6" data-testid="session-reconnecting">
      <div role="status" className="max-w-xl rounded-2xl border border-neutral-200 bg-white p-5">
        <p className="text-sm font-bold text-neutral-900">{t("sessionReconnectTitle")}</p>
        <p className="mt-1 text-sm text-neutral-600">{t("sessionReconnectBody")}</p>
        <Button variant="secondary" className="mt-4" onClick={onRetry}>
          {t("sessionReconnectRetry")}
        </Button>
      </div>
      <Skeleton className="h-48 w-full" />
    </div>
  );
}

/**
 * What stands in for a gated page's content while the first session check is
 * out: a skeleton, or a sentence and a retry while that check keeps failing.
 * The dashboard shell shows it in place of its content (`AD-DSH02`).
 */
export function IdentityPending() {
  const { sessionCheckFailing, retrySessionCheck } = useAuth();
  if (sessionCheckFailing) return <SessionReconnecting onRetry={retrySessionCheck} />;
  return <div aria-busy="true" className="space-y-4 p-6"><Skeleton className="h-12 w-64" /><Skeleton className="h-48 w-full" /></div>;
}

export function ContestTempBoundary({ children, locale }: { children: ReactNode; locale: string }) {
  const { user, isLoading } = useAuth();
  const pathname = usePathname();
  const router = useRouter();
  const temporary = user?.accountKind === "contest_temp";
  const slug = user?.tempContest?.slug;
  // `EX-DAY04`: its contest's warm-up is the one other contest it opens.
  const allowed = !!slug && !!user?.tempContest && tempPageAllowed(pathname, locale, tempContestSlugs(user.tempContest));
  // The dashboard's shell paints on the server and holds its own content
  // (`shellHoldsIdentity`); every other gated page is held here, whole.
  const gated = identityGatedRoute(pathname, locale) && !shellHoldsIdentity(pathname, locale);
  useEffect(() => {
    if (!isLoading && temporary && slug && !allowed) router.replace(`/${locale}/contests/${slug}`);
  }, [isLoading, temporary, slug, allowed, locale, router]);
  // On the account's own surfaces no child client component mounts before the
  // identity is resolved, which keeps dashboard subscriptions, notification and
  // billing reads out of a temp session; the dashboard shell mounts but holds
  // its reads and its content the same way. Public pages are not held: `isLoading`
  // is always true on the server, and holding everything behind it meant no
  // page body was ever server-rendered (2026-09-14, `identityGatedRoute`). A
  // temporary session known to be off its contest is blanked everywhere while
  // the effect above sends it back.
  if (gated && isLoading) return <IdentityPending />;
  if (temporary && !allowed) return <div aria-busy="true" className="space-y-4 p-6"><Skeleton className="h-12 w-64" /><Skeleton className="h-48 w-full" /></div>;
  if (temporary && pathname === `/${locale}/dashboard/profile`) return <><ContestTempNav /><ContestTempProfile /></>;
  return <>{children}</>;
}
